import { strIsNullOrEmpty } from '@fox/atoms/utils/string';
import { IBlockProps } from '@fox/blocks/contracts/block';
import { IMAGE_BLOCK_TYPE } from '@fox/blocks/contracts/block-types';
import { getAttributeValues, transformLegacyProps } from '../../../utils';
import { useFormBlockReRenderer } from '../../use-form-block-re-renderer';
import { useConditionEvaluator } from '../form-block/conditionals/helpers';
import { ContentImageItemConfiguration } from './contracts';

export function ImageBlock({ props, type }: IBlockProps<ContentImageItemConfiguration>) {
	const {
		attributes: { padding }
	} = transformLegacyProps(type, props);
	const { top: paddingTop, left: paddingLeft, right: paddingRight, bottom: paddingBottom } = getAttributeValues(padding);
	if (!props.imageUrl) {
		return null;
	}

	const height = props.imageHeight;
	const width = props.imageWidth;
	const align = props.align;
	const formattedUrl = props.url ? (/^http/.test(props.url) ? props.url : `http://${props.url}`) : '';
	const altText = strIsNullOrEmpty(props?.alt) ? undefined : props?.alt;
	const image = (
		<img
			src={props.imageUrl}
			alt={altText}
			aria-label={altText}
			style={{
				maxWidth: '100%',
				objectFit: 'contain',
				maxHeight: typeof height === 'number' && height > 0 ? height : '100%',
				width: typeof width === 'number' && width > 0 ? width : '100%'
			}}
		/>
	);

	return (
		<div
			style={{
				paddingTop,
				paddingBottom,
				paddingLeft,
				paddingRight,
				lineHeight: 0,
				textAlign: align === 'left' ? 'left' : align === 'right' ? 'right' : 'center'
			}}
		>
			{props.url ? (
				<a href={formattedUrl} target="_blank" style={{ cursor: true ? 'pointer' : 'default' }}>
					{image}
				</a>
			) : (
				image
			)}
		</div>
	);
}

/**
 * Conditionally renders an image block in forms runtime based on evaluation of block props.
 *
 * This component uses the form's conditional logic to determine whether the image block should be rendered.
 * If the condition is not met, nothing is rendered.
 *
 * @param props - The configuration for the image block.
 * @param id - The unique identifier for the block instance.
 * @param type - The block type identifier.
 * @returns The rendered ImageBlock if the condition passes, otherwise null.
 */
export const ConditionalImageBlock = ({ props, id, type }: IBlockProps<ContentImageItemConfiguration>) => {
	useFormBlockReRenderer([], props);
	const shouldRender = useConditionEvaluator({ ...props, name: id });
	if (!shouldRender) return null;
	return <ImageBlock type={type} id={id} props={props} />;
};

export const ConditionalImageBlockRuntime = {
	type: IMAGE_BLOCK_TYPE,
	Component: ConditionalImageBlock
};
